// Éditeur de PROTOCOLE — guide d'entretien, grille d'observation, questionnaire.
//
// Une même structure (sections → items) pour les trois ; ce qui change, ce sont
// les libellés et les champs d'un item : les relances pour un entretien, le
// type de réponse et ses options pour un questionnaire.
//
// On ne part jamais d'une page blanche imposée : un modèle de départ (adapté
// au produit), une proposition de l'IA, ou une page vierge — au choix. Rien de
// ce qui est proposé n'est enregistré sans relecture.
import { useState } from 'react';
import { useSuggestProtocol } from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { AnswerType, ProtocolItem, ProtocolSection, Study, StudyProtocol } from '../api/types';
import { Button, ErrorBanner, IconButton, Select } from '../components/ui';
import { TextArea } from '../write/Forms';
import { ANSWER_TYPES, protocolSize, type KindSpec } from './kinds';
import './studies.css';

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const moved = next.splice(from, 1);
  next.splice(to, 0, ...moved);
  return next;
}

export function ProtocolEditor({
  projectId,
  spec,
  value,
  onChange,
  study,
}: {
  projectId: string;
  spec: KindSpec;
  value: StudyProtocol;
  onChange: (next: StudyProtocol) => void;
  /** Cadrage courant : il nourrit la proposition (objectif, cible…). */
  study: Partial<Pick<Study, 'objective' | 'method' | 'target' | 'context'>>;
}) {
  const suggest = useSuggestProtocol(projectId);
  const [origin, setOrigin] = useState<'ia' | 'modele' | 'repli' | null>(null);
  const [confirming, setConfirming] = useState<'modele' | 'auto' | null>(null);
  const size = protocolSize(value);
  const isSurvey = spec.kind === 'questionnaire';
  const isInterview = spec.kind === 'entretien';

  const setSections = (sections: ProtocolSection[]) => onChange({ ...value, sections });
  const setSection = (i: number, patch: Partial<ProtocolSection>) =>
    setSections(value.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const itemsOf = (si: number) => value.sections[si]?.items ?? [];
  const setItem = (si: number, ii: number, patch: Partial<ProtocolItem>) =>
    setSection(si, { items: itemsOf(si).map((it, j) => (j === ii ? { ...it, ...patch } : it)) });
  const addItem = (si: number) =>
    setSection(si, { items: [...itemsOf(si), isSurvey ? { text: '', answerType: 'ouverte' } : { text: '' }] });
  const addSection = () =>
    setSections([...value.sections, { title: '', items: [isSurvey ? { text: '', answerType: 'ouverte' } : { text: '' }] }]);

  async function propose(source: 'modele' | 'auto') {
    setConfirming(null);
    try {
      const res = await suggest.mutateAsync({ kind: spec.kind, source, study });
      if (res.protocol) {
        onChange(res.protocol);
        // IA demandée mais indisponible : on le DIT, plutôt que de laisser croire
        // qu'un modèle générique a été rédigé pour cette étude.
        setOrigin(source === 'auto' && res.mode === 'modele' ? 'repli' : res.mode);
      }
    } catch {
      /* rendu par le bandeau */
    }
  }

  // Remplacer un protocole déjà écrit se confirme : une proposition ne doit pas
  // effacer en silence des questions rédigées à la main.
  const ask = (source: 'modele' | 'auto') => (size > 0 ? setConfirming(source) : void propose(source));

  let n = 0;
  return (
    <div className="proto-editor">
      <div className="proto-source">
        <div>
          <p className="proto-source-title">
            {size === 0 ? `Comment voulez-vous démarrer votre ${spec.suggestLabel} ?` : `${size} ${size > 1 ? spec.itemPlural : spec.itemLabel.toLowerCase()}`}
          </p>
          {origin === 'ia' ? (
            <p className="zy-texte-muet">Proposé par l’IA à partir de l’objectif et du produit. Relisez chaque question avant de l’utiliser.</p>
          ) : origin === 'repli' ? (
            <p className="zy-texte-muet">L’IA n’est pas disponible pour l’instant : voici le modèle de départ, adapté au produit. À ajuster à votre objectif.</p>
          ) : origin === 'modele' ? (
            <p className="zy-texte-muet">Modèle de départ, adapté au produit quand son contexte est connu. À ajuster à votre objectif.</p>
          ) : null}
        </div>
        <div className="proto-source-actions">
          <Button type="button" variant="ghost" loading={suggest.isPending && suggest.variables?.source === 'modele'} onClick={() => ask('modele')}>
            Partir d’un modèle
          </Button>
          <Button type="button" variant="ghost" loading={suggest.isPending && suggest.variables?.source === 'auto'} onClick={() => ask('auto')}>
            Rédiger avec l’IA
          </Button>
          {size === 0 && value.sections.length === 0 ? (
            <Button type="button" variant="ghost" onClick={addSection}>Page blanche</Button>
          ) : null}
        </div>
      </div>
      {confirming ? (
        <div className="proto-confirm" role="alert">
          <span>Remplacer le contenu actuel ({size} {size > 1 ? spec.itemPlural : spec.itemLabel.toLowerCase()}) par une proposition ?</span>
          <Button type="button" variant="primary" onClick={() => void propose(confirming)}>Remplacer</Button>
          <Button type="button" variant="ghost" onClick={() => setConfirming(null)}>Garder</Button>
        </div>
      ) : null}
      {suggest.error ? (
        <ErrorBanner>{suggest.error instanceof ApiRequestError ? suggest.error.message : 'La proposition a échoué.'}</ErrorBanner>
      ) : null}

      {value.sections.length === 0 && size === 0 ? null : (
        <>
          <div className="field-row">
            <label className="zy-field proto-duration">
              <span className="zy-field__label">{spec.durationLabel}</span>
              <input
                className="zy-input"
                type="number"
                min={1}
                max={600}
                value={value.durationMin ?? ''}
                onChange={(e) => onChange({ ...value, durationMin: e.target.value === '' ? null : Number(e.target.value) })}
              />
            </label>
          </div>
          {spec.kind === 'observation' ? (
            <TextArea
              label="Lieux et situations à observer"
              value={value.places ?? ''}
              onChange={(v) => onChange({ ...value, places: v })}
              rows={2}
              placeholder="Magasin central, dépôt de chantier, camion atelier — en début de journée"
            />
          ) : null}
          <TextArea label={spec.introLabel} value={value.intro} onChange={(v) => onChange({ ...value, intro: v })} rows={3} />

          <ol className="proto-sections">
            {value.sections.map((s, si) => (
              <li key={si} className="proto-section">
                <div className="proto-section-head">
                  <input
                    className="zy-input proto-section-title"
                    value={s.title}
                    onChange={(e) => setSection(si, { title: e.target.value })}
                    placeholder={`${spec.sectionLabel} ${si + 1}`}
                    aria-label={`Titre — ${spec.sectionLabel.toLowerCase()} ${si + 1}`}
                  />
                  <span className="proto-tools">
                    <IconButton icon="arrow-up" onClick={() => setSections(move(value.sections, si, si - 1))} disabled={si === 0} label={`Monter ${spec.sectionLabel.toLowerCase()} ${si + 1}`} />
                    <IconButton icon="arrow-down" onClick={() => setSections(move(value.sections, si, si + 1))} disabled={si === value.sections.length - 1} label={`Descendre ${spec.sectionLabel.toLowerCase()} ${si + 1}`} />
                    <IconButton icon="close" danger onClick={() => setSections(value.sections.filter((_, j) => j !== si))} label={`Retirer ${spec.sectionLabel.toLowerCase()} ${si + 1}`} />
                  </span>
                </div>

                <ul className="proto-items">
                  {s.items.map((it, ii) => {
                    n += 1;
                    const num = n;
                    return (
                      <li key={ii} className="proto-item">
                        <span className="proto-num" aria-hidden="true">{num}</span>
                        <div className="proto-item-body">
                          <textarea
                            className="zy-input proto-item-text"
                            rows={2}
                            value={it.text}
                            onChange={(e) => setItem(si, ii, { text: e.target.value })}
                            placeholder={spec.itemPlaceholder}
                            aria-label={`${spec.itemLabel} ${num}`}
                            autoFocus={it.text === '' && ii === s.items.length - 1 && ii > 0}
                          />
                          {isInterview ? (
                            <input
                              className="zy-input proto-followups"
                              value={it.followups ?? ''}
                              onChange={(e) => setItem(si, ii, { followups: e.target.value })}
                              placeholder="Relances : Pouvez-vous me donner un exemple ? Et ensuite ?"
                              aria-label={`Relances de la question ${num}`}
                            />
                          ) : null}
                          {isSurvey ? (
                            <div className="proto-answer">
                              <Select
                               
                                value={it.answerType ?? 'ouverte'}
                                onChange={(e) => setItem(si, ii, { answerType: e.target.value as AnswerType })}
                                aria-label={`Type de réponse de la question ${num}`}
                              >
                                {ANSWER_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                              </Select>
                              {it.answerType && it.answerType !== 'ouverte' && it.answerType !== 'oui_non' ? (
                                <input
                                  className="zy-input"
                                  value={it.options ?? ''}
                                  onChange={(e) => setItem(si, ii, { options: e.target.value })}
                                  placeholder={it.answerType === 'echelle' ? '1 (pas du tout) à 5 (tout à fait)' : 'Option A ; Option B ; Autre'}
                                  aria-label={`Options de la question ${num}`}
                                />
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                        <span className="proto-tools">
                          <IconButton icon="arrow-up" onClick={() => setSection(si, { items: move(s.items, ii, ii - 1) })} disabled={ii === 0} label={`Monter ${spec.itemLabel.toLowerCase()} ${num}`} />
                          <IconButton icon="arrow-down" onClick={() => setSection(si, { items: move(s.items, ii, ii + 1) })} disabled={ii === s.items.length - 1} label={`Descendre ${spec.itemLabel.toLowerCase()} ${num}`} />
                          <IconButton icon="close" danger onClick={() => setSection(si, { items: s.items.filter((_, j) => j !== ii) })} label={`Retirer ${spec.itemLabel.toLowerCase()} ${num}`} />
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <Button variant="ghost" size="s" icon="plus" type="button" className="feature-add" onClick={() => addItem(si)}>
                  {spec.itemLabel}
                </Button>
              </li>
            ))}
          </ol>
          <button type="button" className="feature-add proto-add-section" onClick={addSection}>
            + {spec.sectionLabel}
          </button>

          <TextArea
            label="Conclusion"
            value={value.closing}
            onChange={(v) => onChange({ ...value, closing: v })}
            rows={2}
            placeholder="Remercier, expliquer la suite de l’étude."
          />
        </>
      )}
    </div>
  );
}

/** Liste des tâches d'un test : une ligne = une action à faire réaliser. */
export function TaskListEditor({
  spec,
  tasks,
  onChange,
}: {
  spec: KindSpec;
  tasks: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="proto-editor">
      <ol className="proto-items proto-tasks">
        {tasks.map((t, i) => (
          <li key={i} className="proto-item">
            <span className="proto-num" aria-hidden="true">{i + 1}</span>
            <div className="proto-item-body">
              <input
                className="zy-input"
                value={t}
                onChange={(e) => onChange(tasks.map((x, j) => (j === i ? e.target.value : x)))}
                placeholder={spec.itemPlaceholder}
                aria-label={`Tâche ${i + 1}`}
                autoFocus={t === '' && i > 0 && i === tasks.length - 1}
              />
            </div>
            <span className="proto-tools">
              <IconButton icon="arrow-up" onClick={() => onChange(move(tasks, i, i - 1))} disabled={i === 0} label={`Monter la tâche ${i + 1}`} />
              <IconButton icon="arrow-down" onClick={() => onChange(move(tasks, i, i + 1))} disabled={i === tasks.length - 1} label={`Descendre la tâche ${i + 1}`} />
              <IconButton icon="close" danger onClick={() => onChange(tasks.filter((_, j) => j !== i))} label={`Retirer la tâche ${i + 1}`} />
            </span>
          </li>
        ))}
      </ol>
      <Button variant="ghost" size="s" icon="plus" type="button" className="feature-add" onClick={() => onChange([...tasks, ''])}>
        Tâche
      </Button>
    </div>
  );
}
